import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sparkles, BookOpen, TrendingUp, Globe, Lightbulb, ArrowRight, Search, Database, Users } from 'lucide-react';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';

const API = window.location.origin + '/api';

const Resources = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const response = await axios.get(`${API}/resources`);
        setPages(response.data.pages || []);
      } catch (error) {
        console.error('Failed to load resources:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPages();
  }, []);

  // Group pages by type
  const overviewPages = pages.filter(p => p.type === 'overview');
  const nichePages = pages.filter(p => p.type === 'niche');
  const regionPages = pages.filter(p => p.type === 'region' || p.type === 'niche_region');
  const guidePages = pages.filter(p => p.type === 'guide');

  const getTypeIcon = (type) => {
    switch (type) {
      case 'overview': return <TrendingUp className="w-5 h-5" />;
      case 'niche': return <Search className="w-5 h-5" />;
      case 'region': 
      case 'niche_region': return <Globe className="w-5 h-5" />;
      case 'guide': return <Lightbulb className="w-5 h-5" />;
      default: return <BookOpen className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'overview': return 'bg-indigo-100 text-indigo-700';
      case 'niche': return 'bg-emerald-100 text-emerald-700';
      case 'region': 
      case 'niche_region': return 'bg-amber-100 text-amber-700';
      case 'guide': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title="YouTube Sponsorship Insights & Resources"
        description="Data-driven insights on YouTube sponsorships. Discover which brands sponsor creators, sponsorship trends by niche, and guides to landing brand deals."
        path="/resources"
      />

      {/* Navigation - Product First */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="home-logo-link">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/explore-brands">
              <Button variant="ghost" size="sm">Discover Brands</Button>
            </Link>
            <Link to="/brands/discover">
              <Button variant="ghost" size="sm">Discover Creators</Button>
            </Link>
            <Link to="/leaderboards">
              <Button variant="ghost" size="sm">Leaderboards</Button>
            </Link>
            <Link to="/">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm">
                Analyze Sponsorships
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-indigo-50 to-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-medium mb-4">
            <BookOpen className="w-4 h-4" />
            <span>Sponsorship Intelligence</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}} data-testid="resources-h1">
            YouTube Sponsorship Insights
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Data-driven insights on brands sponsoring YouTube creators. Discover trends, find sponsors in your niche, 
            and learn how to land brand partnerships.
          </p>
          
          {/* Strong Product CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/explore-brands">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 h-11 font-semibold">
                <Database className="w-4 h-4 mr-2" />
                Explore Real Sponsor Data
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/brands/discover">
              <Button variant="outline" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 px-6 h-11 font-semibold">
                <Users className="w-4 h-4 mr-2" />
                Find Creators Working with Brands
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <Card key={i} className="p-6 animate-pulse">
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-slate-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                </Card>
              ))}
            </div>
          ) : (
            <>
              {/* Overview Section */}
              {overviewPages.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                    <TrendingUp className="w-6 h-6 text-indigo-600" />
                    Sponsorship Overview
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {overviewPages.map((page) => (
                      <Link key={page.slug} to={`/resources/${page.slug}`}>
                        <Card className="p-6 hover:shadow-lg transition-shadow h-full" data-testid={`resource-card-${page.slug}`}>
                          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium mb-3 ${getTypeColor(page.type)}`}>
                            {getTypeIcon(page.type)}
                            <span>Overview</span>
                          </div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>
                            {page.title}
                          </h3>
                          <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                            {page.meta_description}
                          </p>
                          <span className="text-indigo-600 text-sm font-medium flex items-center gap-1">
                            Read more <ArrowRight className="w-4 h-4" />
                          </span>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Niche-Specific Section */}
              {nichePages.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                    <Search className="w-6 h-6 text-emerald-600" />
                    Sponsors by Niche
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {nichePages.map((page) => (
                      <Link key={page.slug} to={`/resources/${page.slug}`}>
                        <Card className="p-6 hover:shadow-lg transition-shadow h-full" data-testid={`resource-card-${page.slug}`}>
                          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium mb-3 ${getTypeColor(page.type)}`}>
                            {getTypeIcon(page.type)}
                            <span>{page.niche || 'Niche'}</span>
                          </div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>
                            {page.title}
                          </h3>
                          <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                            {page.meta_description}
                          </p>
                          <span className="text-indigo-600 text-sm font-medium flex items-center gap-1">
                            Read more <ArrowRight className="w-4 h-4" />
                          </span>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* India-Specific Section */}
              {regionPages.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                    <Globe className="w-6 h-6 text-amber-600" />
                    India Sponsorship Insights
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {regionPages.map((page) => (
                      <Link key={page.slug} to={`/resources/${page.slug}`}>
                        <Card className="p-6 hover:shadow-lg transition-shadow h-full" data-testid={`resource-card-${page.slug}`}>
                          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium mb-3 ${getTypeColor(page.type)}`}>
                            {getTypeIcon(page.type)}
                            <span>India</span>
                          </div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>
                            {page.title}
                          </h3>
                          <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                            {page.meta_description}
                          </p>
                          <span className="text-indigo-600 text-sm font-medium flex items-center gap-1">
                            Read more <ArrowRight className="w-4 h-4" />
                          </span>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Guides Section */}
              {guidePages.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                    <Lightbulb className="w-6 h-6 text-purple-600" />
                    Creator Guides
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {guidePages.map((page) => (
                      <Link key={page.slug} to={`/resources/${page.slug}`}>
                        <Card className="p-6 hover:shadow-lg transition-shadow h-full" data-testid={`resource-card-${page.slug}`}>
                          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium mb-3 ${getTypeColor(page.type)}`}>
                            {getTypeIcon(page.type)}
                            <span>Guide</span>
                          </div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>
                            {page.title}
                          </h3>
                          <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                            {page.meta_description}
                          </p>
                          <span className="text-indigo-600 text-sm font-medium flex items-center gap-1">
                            Read more <ArrowRight className="w-4 h-4" />
                          </span>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="bg-white border-t border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
              Data-Driven Sponsorship Intelligence
            </h2>
            <p className="text-slate-600 max-w-3xl leading-relaxed">
              Sponsiq analyzes thousands of YouTube videos to track brand sponsorships in real-time. Our resources help creators 
              understand the sponsorship landscape, identify brands actively investing in influencer marketing, and optimize their 
              approach to landing brand deals.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
                For YouTube Creators
              </h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/brands/discover" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Find Creators Getting Sponsored
                  </Link>
                </li>
                <li>
                  <Link to="/explore-brands" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Browse Sponsoring Brands
                  </Link>
                </li>
                <li>
                  <Link to="/leaderboards" className="text-indigo-600 hover:text-indigo-800 hover:underline">
                    → Creator Leaderboards
                  </Link>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
                Popular Topics
              </h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• Brands sponsoring tech YouTubers</li>
                <li>• Indian creator sponsorship landscape</li>
                <li>• Finance app influencer partnerships</li>
                <li>• Beauty brand creator campaigns</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
                Get Started
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">
                Analyze your YouTube channel to discover your sponsorship potential and find brands looking for creators like you.
              </p>
              <Link to="/">
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  Analyze My Channel
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer showDisclaimer={true} />
    </div>
  );
};

export default Resources;
